import { useTranslation } from 'react-i18next';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/shared/components/ui/tabs';
import { cn } from '@/core/lib/utils';
import { useUpdateProjectTemplateStructure } from '../hooks/useProjectTemplateMutations';
import { TemplateTasksTab } from './TemplateTasksTab';
import { TemplateStatusesTab } from './TemplateStatusesTab';
import { TemplateViewsTab } from './TemplateViewsTab';
import { TemplateFieldsTab } from './TemplateFieldsTab';
import { TemplateTagsTab } from './TemplateTagsTab';
import type { ProjectTemplate, TemplateBoard, TemplateCustomField, TemplateTag } from '../types';

interface TemplatePreviewPanelProps {
  template: ProjectTemplate;
}

/**
 * Read-only for system presets; inline-editable for tenant-owned templates.
 * Every add/remove action recomputes the full structure payload and saves it
 * immediately via `useUpdateProjectTemplateStructure` — same instant-persist
 * pattern as `boards`/`tags` inline creation, no separate "save" step.
 *
 * The editor only ever targets `boards[0]` — creating/editing more than one board has no
 * design (only `save-as-template` produces multi-board templates, and those aren't
 * structurally editable in this dialog per the confirmed scope).
 */
export function TemplatePreviewPanel({ template }: TemplatePreviewPanelProps) {
  const { t } = useTranslation('projectTemplates');
  const editable = !template.isSystem;
  const updateStructure = useUpdateProjectTemplateStructure(template.id);
  const board = template.boards[0];

  const taskCount =
    board.groups.reduce((sum, g) => sum + g.tasks.length, 0) + board.ungroupedTasks.length;
  const stats = [
    t('preview.stats.tasks', { count: taskCount }),
    t('preview.stats.groups', { count: board.groups.length }),
    t('preview.stats.statuses', { count: board.statuses.length }),
    t('preview.stats.views', { count: board.views.length }),
  ].join(' · ');

  function saveBoard(patch: Partial<TemplateBoard>) {
    const updatedBoard: TemplateBoard = { ...board, ...patch };
    updateStructure.mutate({
      boards: template.boards.map((b, i) => (i === 0 ? updatedBoard : b)),
      customFields: template.customFields,
      tags: template.tags,
    });
  }

  function saveRoot(patch: { customFields?: TemplateCustomField[]; tags?: TemplateTag[] }) {
    updateStructure.mutate({
      boards: template.boards,
      customFields: patch.customFields ?? template.customFields,
      tags: patch.tags ?? template.tags,
    });
  }

  const tabTriggerClassName =
    'rounded-none border-b-2 border-transparent px-3 py-2 -mb-px text-muted-foreground ' +
    'data-[state=active]:border-primary data-[state=active]:bg-transparent data-[state=active]:text-foreground data-[state=active]:shadow-none';

  return (
    <div className="flex h-full flex-col" data-testid="project-templates-preview-panel">
      <div className="flex items-baseline justify-between gap-2 pb-3">
        <p className="text-sm font-semibold text-foreground">{t('preview.title')}</p>
        <p className="text-xs text-muted-foreground">{stats}</p>
      </div>

      <Tabs defaultValue="tasks" className="flex h-full flex-col">
        <TabsList className="w-full justify-start rounded-none border-b border-border bg-transparent p-0">
          <TabsTrigger
            value="tasks"
            className={cn(tabTriggerClassName)}
            data-testid="project-templates-preview-tab-tasks"
          >
            {t('preview.tabs.tasks')}
          </TabsTrigger>
          <TabsTrigger
            value="statuses"
            className={cn(tabTriggerClassName)}
            data-testid="project-templates-preview-tab-statuses"
          >
            {t('preview.tabs.statuses')}
          </TabsTrigger>
          <TabsTrigger
            value="views"
            className={cn(tabTriggerClassName)}
            data-testid="project-templates-preview-tab-views"
          >
            {t('preview.tabs.views')}
          </TabsTrigger>
          <TabsTrigger
            value="fields"
            className={cn(tabTriggerClassName)}
            data-testid="project-templates-preview-tab-fields"
          >
            {t('preview.tabs.fields')}
          </TabsTrigger>
          <TabsTrigger
            value="tags"
            className={cn(tabTriggerClassName)}
            data-testid="project-templates-preview-tab-tags"
          >
            {t('preview.tabs.tags')}
          </TabsTrigger>
        </TabsList>

        <div className="flex-1 overflow-y-auto py-3">
          <TabsContent value="tasks">
            <TemplateTasksTab
              groups={board.groups}
              editable={editable}
              isPending={updateStructure.isPending}
              onAddGroup={(name) =>
                saveBoard({
                  groups: [...board.groups, { name, order: board.groups.length, tasks: [] }],
                })
              }
              onRemoveGroup={(groupIndex) =>
                saveBoard({ groups: board.groups.filter((_, i) => i !== groupIndex) })
              }
              onAddTask={(groupIndex, taskValues) =>
                saveBoard({
                  groups: board.groups.map((g, i) =>
                    i === groupIndex ? { ...g, tasks: [...g.tasks, taskValues] } : g,
                  ),
                })
              }
              onRemoveTask={(groupIndex, taskIndex) =>
                saveBoard({
                  groups: board.groups.map((g, i) =>
                    i === groupIndex
                      ? { ...g, tasks: g.tasks.filter((_, ti) => ti !== taskIndex) }
                      : g,
                  ),
                })
              }
            />
          </TabsContent>

          <TabsContent value="statuses">
            <TemplateStatusesTab
              statuses={board.statuses}
              editable={editable}
              isPending={updateStructure.isPending}
              onAdd={(values) =>
                saveBoard({
                  statuses: [...board.statuses, { ...values, order: board.statuses.length }],
                })
              }
              onRemove={(index) =>
                saveBoard({ statuses: board.statuses.filter((_, i) => i !== index) })
              }
            />
          </TabsContent>

          <TabsContent value="views">
            <TemplateViewsTab
              views={board.views}
              editable={editable}
              isPending={updateStructure.isPending}
              onAdd={(view) => saveBoard({ views: [...board.views, view] })}
              onRemove={(index) => saveBoard({ views: board.views.filter((_, i) => i !== index) })}
            />
          </TabsContent>

          <TabsContent value="fields">
            <TemplateFieldsTab
              fields={template.customFields}
              editable={editable}
              isPending={updateStructure.isPending}
              onAdd={(values) =>
                saveRoot({
                  customFields: [
                    ...template.customFields,
                    { ...values, options: values.options ?? null },
                  ],
                })
              }
              onRemove={(index) =>
                saveRoot({ customFields: template.customFields.filter((_, i) => i !== index) })
              }
            />
          </TabsContent>

          <TabsContent value="tags">
            <TemplateTagsTab
              tags={template.tags}
              editable={editable}
              isPending={updateStructure.isPending}
              onAdd={(values) => saveRoot({ tags: [...template.tags, values] })}
              onRemove={(index) => saveRoot({ tags: template.tags.filter((_, i) => i !== index) })}
            />
          </TabsContent>
        </div>
      </Tabs>
    </div>
  );
}
